/**
 * API client. The access token lives in memory only; the refresh token is an
 * httpOnly cookie the browser sends by itself. A 401 triggers one silent
 * refresh, and concurrent callers share it.
 */
export interface ApiErrorShape {
  message: string | string[];
  error?: string;
  statusCode: number;
}

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code, e.g. PERIOD_LOCKED (RFC 9457 extension). */
  readonly code: string | undefined;
  /** Field messages from the 422 validation pipe, ready to show inline. */
  readonly fieldMessages: string[];
  /** Field-level errors: field path → message. */
  readonly fields: Record<string, string>;
  /** The raw error payload, for extra fields such as `warnings` on a 409. */
  readonly body: Record<string, unknown>;

  constructor(status: number, message: string, fieldMessages: string[] = [], code?: string, fields: Record<string, string> = {}, body: Record<string, unknown> = {}) {
    super(message);
    this.body = body;
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldMessages = fieldMessages;
    this.fields = fields;
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared once settled, so callers that 401 during the refresh share it
      // rather than each starting another with the same cookie.
      queueMicrotask(() => (refreshing = null));
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the silent refresh, e.g. for the login call itself. */
  anonymous?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = (): Promise<Response> =>
    fetch(`/api/v1${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(accessToken && !options.anonymous ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

  let response = await send();

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const shape = payload as (ApiErrorShape & { code?: string; errors?: { field?: string; message: string }[] }) | null;
    const messages = Array.isArray(shape?.message)
      ? shape.message
      : shape?.message
        ? [shape.message]
        : [`Request failed (${response.status})`];
    const fields: Record<string, string> = {};
    for (const e of shape?.errors ?? []) if (e.field && !fields[e.field]) fields[e.field] = e.message;
    throw new ApiError(response.status, messages[0], messages, shape?.code, fields, (payload ?? {}) as Record<string, unknown>);
  }

  return payload as T;
}
